import uuid from 'uuid/v4';
import { getIdToken } from '../services/ServiceManager';
import {
  saveUploadedAsset,
  removeAsset,
  updateAssetTags
} from '../services/UploadAssetManager';
import { createVideoForAccount } from '../fbApi/Videos';
import buckets from './Buckets';
import { api as apiBuilder } from '../utils/apiBuilder';
import { getUserEmail } from '../utils/user';

export const getSignedUrl = async (filename, type) => {
  const operation = 'asset/upload/token/';
  const endpoint = apiBuilder.buildApi(operation);
  const token = await new Promise(async (resolve, reject) => {
    await getIdToken().then(async (result) => resolve(result));
    reject(null);
  });

  const data = {
    filename,
    type
  };

  const formatedData = JSON.stringify(data);
  const result = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      AlexiaAuth: token
    },
    method: 'POST',
    body: formatedData
  });

  const signedUrl = await result.json();
  return signedUrl;
};

export const removeFile = async (filename) => {
  const operation = 'asset/delete/';
  const endpoint = apiBuilder.buildApi(operation);

  const token = await new Promise(async (resolve, reject) => {
    await getIdToken().then(async (result) => resolve(result));
    reject(null);
  });

  const data = {
    filename
  };

  const formatedData = JSON.stringify(data);
  const result = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      AlexiaAuth: token
    },
    method: 'DELETE',
    body: formatedData
  });

  const removeFileResult = await result.json();
  return removeFileResult;
};

export const updateFile = async (assetId, tags) => {
  // const resultDB = await saveUploadedAsset(assetData);
  const resultDB = await updateAssetTags(assetId, tags);
  const { statusCode, assetTags, error } = resultDB;

  return statusCode === 200
    ? {
        assetTags,
        errorMessage: ''
      }
    : {
        assetTags: null,
        errorMessage: error
      };
};

export const removeAssetFromDB = async (id) => {
  const assetData = { id };

  const resultDB = await removeAsset(assetData);
  return { status: resultDB.statusCode === 200 ? 'Success' : 'Error' };
};

/**
 * When we upload file to S3 or facebook, we will replace the actual filename with guid.
 * This guid will be saved in DB along with the actual filename.
 *
 * NOTE
 * - Whenever we need to display the actual filename, we need to get it from our DB.
 * - The token passed into this function, it is for facebook upload and declared in AssetGallery/index.js.
 * This token will depends on the environment defined in Domain.
 * - fbWorker is web worker that is responsible for getting uploaded video thumbnail in background thread.
 * If fbWorker is null, normal process will be executed.
 *
 */
export const uploadFile = async (
  file,
  accountId,
  tags,
  token,
  user,
  fbWorker = null
) => {
  // if (fbWorker) fbWorker.postMessage({ videoId: 123, token: 'token testing' });

  // return { statusCode: 500 };

  const uniqueId = uuid();
  const filename = file.name;

  // get the file extension
  const splitted = filename.split('.');
  const extension = splitted.slice(-1);

  const newFilename = `${uniqueId}.${extension}`;
  const signedUrl = await getSignedUrl(newFilename, file.type);
  const result = await fetch(signedUrl.tokenData, {
    headers: {
      'Content-Type': file.type
    },
    method: 'PUT',
    body: file
  });

  // S3 bucket upload error
  if (result.status >= 300) {
    return {
      statusCode: result.status,
      result: null,
      error: `Error uploading ${filename} to the bucket.\n`
    };
  }

  const assetData = {
    filename,
    guid: uniqueId,
    type: file.type,
    account_id: accountId,
    tags,
    uploaded_by: `${getUserEmail(user)}` // This must not be empty in DB. So user must validated prior to this function call. (AssetGallery/index.js)
  };

  let resultDB = null;

  if (file.type.indexOf('image') !== -1) {
    /** Example
   *** success ***
   {
      "statusCode": 200,
      "data": {
          "assetId": 122,
          "fbAssetId": 120,
          "tags": [
              {
                  "category": "account",
                  "required": false,
                  "value": "Fiore VW"
              },
              {
                  "category": "account",
                  "required": false,
                  "value": "VW of Orchard Park"
              }
          ]
    }

   *** fail ***
    {
      "statusCode": 500,
      "error": "[Lambda] Failed to process request: aa"
    }
   */
    resultDB = await saveUploadedAsset(assetData);

    return resultDB.statusCode === 200
      ? {
          statusCode: resultDB.statusCode,
          result: {
            message: `${filename} has been uploaded successfullly.\n`,
            asset: resultDB.data.asset // the structure might have to be changed...
          },
          error: null
        }
      : {
          statusCode: resultDB.statusCode,
          result: null,
          error: `Error saving uploaded assets(${filename}) in database.\n`
        };
  }

  const fileUrl = buckets[window.location.hostname] + newFilename;
  const videoStillUrl = getVideoStillFromS3(fileUrl);
  const videoId = await uploadVideoToFacebook(accountId, fileUrl, token);
  if (!videoId) {
    return {
      statusCode: 500,
      result: null,
      error: `Error uploading ${filename} to Facebook.\n`
    };
  }
  if (videoStillUrl) {
    console.log(videoId, videoStillUrl);
    assetData.facebook_asset_id = videoId;
    assetData.video_thumbnail = videoStillUrl;
  }
  if (fbWorker) {
    console.log('about to postMessage... with assetData:', assetData);

    resultDB = await saveUploadedAsset(assetData);

    const { statusCode, data } = resultDB || {};
    return statusCode === 200
      ? {
          statusCode,
          result: {
            message: `${filename} has been uploaded successfullly.\n`,
            asset: data.asset // the structure might have to be changed...
          },
          // workerInProgress,
          error: null
        }
      : {
          statusCode: resultDB.statusCode,
          result: null,
          // workerInProgress,
          error: `Error saving uploaded assets(${filename}) in database.\n`
        };
  }

  /** Example
   *** success ***
   {
      "statusCode": 200,
      "data": {
          "assetId": 122,
          "fbAssetId": 120,
          "tags": [
              {
                  "category": "account",
                  "required": false,
                  "value": "Fiore VW"
              },
              {
                  "category": "account",
                  "required": false,
                  "value": "VW of Orchard Park"
              }
          ]
    }

   *** fail ***
    {
      "statusCode": 500,
      "error": "[Lambda] Failed to process request: aa"
    }
   */
  resultDB = await saveUploadedAsset(assetData);

  return resultDB.statusCode === 200
    ? {
        statusCode: resultDB.statusCode,
        result: {
          message: `${filename} has been uploaded successfullly.\n`,
          asset: resultDB.data.asset // the structure might have to be changed...
        },
        error: null
      }
    : {
        statusCode: resultDB.statusCode,
        result: null,
        error: `Error saving uploaded assets(${filename}) in database.\n`
      };
};

const getVideoStillFromS3 = (s3FilePath) => {
  const s3StillPath = s3FilePath.replace('mp4', 'jpg');
  console.log(s3StillPath);
  return s3StillPath;
};

const uploadVideoToFacebook = async (accountId, file, token) => {
  const parameters = {
    accountId,
    file_url: file
  };

  const result = await createVideoForAccount(parameters, token);

  return result.id;
};



// WEBPACK FOOTER //
// ./src/fileUploader/FileUploader.v2.js